import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import styled, { css } from "styled-components";
import { Header } from "../../components/Header";
import PageWrapper from "../../components/PageWrapper";
import { PrimaryFooter } from "../../components/PrimaryFooter";
import { Stack } from "../../components/Stack";
import config from "../../config";
import { useNhsLoginAuthMutation } from "../../graphql/hooks/useNhsLoginAuthMutation";
import { SetupStatus, NHSLoginError } from "../../graphql/types/global";
import { authActions } from "../../store/auth";
import * as queryString from "../../utils/queryString";
import { authenticateWithIntercom } from "../../login";
import { Base64 } from "js-base64";
import DeprecatedButton from "../../components/DeprecatedButton";
import Icon from "../../components/Icon";
import { WarningIcon } from "@echo-health/icons-web";
import { LoadingIndicator } from "../../design-system/components/LoadingIndicator";
import { Heading } from "../../design-system/components/Heading";
import { Text } from "../../design-system/components/Text";
import { Card } from "../../design-system/components/Card";
import { useNhsLoginSetupMutation } from "../../graphql/hooks/useNhsLoginSetupMutation";
import { trackEvent } from "../../analytics";
import Cookies from "js-cookie";
import { isClient } from "../../global";
import * as Sentry from "@sentry/browser";

const CardWrapper = styled.div`
  ${({ theme }) => css`
    max-width: 500px;
    margin: auto;
    display: flex;

    @media screen and (max-width: ${theme.breakpoints.s}) {
      width: 100%;
    }
  `}
`;

const LoaderCard = styled.div`
  display: flex;
  flex: 1;
  min-height: 500px;
  background: ${p => p.theme.color.white};
  box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.05);
  border-radius: 5px;
  padding: ${p => p.theme.spacing.m};
  justify-content: center;
  align-items: center;
`;

enum AuthSource {
  IOS = 1,
  ANDROID,
  WEB
}

const NHSLoginAuth: React.FC<RouteComponentProps> = props => {
  const [hasGenericError, setHasGenericError] = useState(false);
  const [hasUnavailableError, setHasUnavailableError] = useState(false);
  const [nokError, setNokError] = useState("");
  const dispatch = useDispatch();

  let hasOAuthState = false;
  if (isClient()) {
    if (Cookies.get("nhs_login_auth_state")) {
      hasOAuthState = Cookies.get("nhs_login_auth_state") !== undefined;
    }
  }

  let authSource = hasOAuthState ? AuthSource.WEB : AuthSource.IOS;
  try {
    const returnQuery = queryString.decode(props.history.location.search);
    const returnedState =
      returnQuery.state && returnQuery.state[0] ? returnQuery.state[0] : "";
    const decoded = Base64.decode(returnedState);
    if (decoded.startsWith("android-")) {
      authSource = AuthSource.ANDROID;
    }
  } catch (error) {
    Sentry.captureException(error);
  }

  const [
    nhsLoginSetupMutation,
    { loading: isNhsSetupLoading, error: nhsSetupError }
  ] = useNhsLoginSetupMutation({
    onCompleted: response => {
      const hasNoData =
        (!response || !response.nhsLoginSetup) && !isNhsSetupLoading;
      const hasResponseError = response && response.nhsLoginSetup.error;
      const hasMissingData =
        !response.nhsLoginSetup.account || !response.nhsLoginSetup.tokens;

      if (hasNoData || hasResponseError || hasMissingData || nhsSetupError) {
        setHasGenericError(true);
      }

      const account = response.nhsLoginSetup.account!;

      authenticateWithIntercom({
        accountId: account.id,
        intercomId: account.intercomId,
        email: account.email,
        firstName: account.firstName,
        lastName: account.lastName,
        dispatch,
        intercomUserHash: account.intercomUserHash
      });

      dispatch(authActions.setNHSLoginHasIM1Enabled(account.hasIM1Enabled));

      props.history.replace("/nhs-login/confirm");
    },
    onError: () => {
      setHasGenericError(true);
    }
  });

  const [
    nhsLoginAuthMutation,
    { loading: isNhsAuthLoading, error: nhsAuthError }
  ] = useNhsLoginAuthMutation({
    onCompleted: response => {
      if (
        ((!response || !response.nhsLoginAuth) && !isNhsAuthLoading) ||
        nhsAuthError
      ) {
        trackEvent("nhs_login_error_pageview");
        setHasGenericError(true);
        return;
      }

      if (response.nhsLoginAuth.error !== null) {
        if (
          response.nhsLoginAuth.error ===
            NHSLoginError.NHS_LOGIN_ERROR_PATIENT_IS_NOK &&
          response.nhsLoginAuth.errorDescription
        ) {
          trackEvent("nhs_login_account_creation_error_pageview");
          return setNokError(response.nhsLoginAuth.errorDescription);
        } else {
          trackEvent("nhs_login_error_pageview");
          return setHasGenericError(true);
        }
      }

      // If account exists with tokens, login
      const nhsLoginAuth = response.nhsLoginAuth;
      const account = nhsLoginAuth.account;
      if (account && nhsLoginAuth.tokens && nhsLoginAuth.tokens.accessToken) {
        trackEvent("nhs_login_success");

        authenticateWithIntercom({
          accountId: account.id,
          intercomId: account.intercomId,
          email: account.email,
          firstName: account.firstName,
          lastName: account.lastName,
          dispatch,
          intercomUserHash: account.intercomUserHash
        });

        const query = queryString.decode(props.location.search);
        props.history.replace(query.returnUrl ? query.returnUrl[0] : "/");
      }

      if (
        !response.nhsLoginAuth.setupToken ||
        !response.nhsLoginAuth.setupStatus
      ) {
        trackEvent("nhs_login_error_pageview");
        setHasGenericError(true);
        return;
      }

      dispatch(
        authActions.setNHSSetupToken({
          token: response.nhsLoginAuth.setupToken
        })
      );

      if (
        response.nhsLoginAuth.setupStatus ===
        SetupStatus.STATUS_EXISTING_ACCOUNT
      ) {
        trackEvent("nhs_login_success");

        // If it's an existing account, call the NHSLoginSetup mutation and then navigate to the confirmation screen
        nhsLoginSetupMutation({
          variables: {
            setupToken: response.nhsLoginAuth.setupToken
          }
        });
      } else if (
        response.nhsLoginAuth.setupStatus === SetupStatus.STATUS_NEW_ACCOUNT
      ) {
        trackEvent("nhs_login_success");

        // If it's a new account, go to account creation consent screen
        props.history.replace("/nhs-login/create");
      }
    },
    onError: () => {
      trackEvent("nhs_login_error_pageview");
      setHasGenericError(true);
    }
  });

  useEffect(() => {
    if (hasOAuthState) {
      const state = Cookies.get("nhs_login_auth_state");
      const nonce = Cookies.get("nhs_login_nonce");
      const returnQuery = queryString.decode(props.history.location.search);
      const returnedState = returnQuery.state && returnQuery.state[0];
      const authCode = returnQuery.code && returnQuery.code[0];
      const errorCode = returnQuery.error && returnQuery.error[0];

      if (returnedState !== state || !authCode || !nonce) {
        // If the state doesn't match, then either the user has come from the mobile app or the state has changed
        setTimeout(() => {
          trackEvent("nhs_login_error_pageview");
          setHasGenericError(true);
        }, 1000);
        return;
      }

      if (errorCode === "temporarily_unavailable") {
        trackEvent("nhs_login_unavaliable_pageview");
        setHasUnavailableError(true);
        return;
      }

      if (returnedState === state) {
        nhsLoginAuthMutation({
          variables: {
            authCode,
            redirectUri: config.NHS_LOGIN_REDIRECT_URL,
            nonce
          }
        });
      }
    }
  }, [hasOAuthState, nhsLoginAuthMutation, props.history.location.search]);

  let content = null;

  if (nokError) {
    content = (
      <Card>
        <Icon type={WarningIcon} size="l" color="orange40" margin="0 0 m 0" />
        <Heading margin="0 0 s 0">Sorry, something went wrong</Heading>
        <Text margin="0 0 s 0">{nokError}</Text>
        <Text margin="0 0 m 0">
          If you do not recognise the account holder then please{" "}
          <DeprecatedButton
            variant="tertiary"
            color="blue"
            className="openIntercom"
          >
            contact our support team
          </DeprecatedButton>
          .
        </Text>
        <DeprecatedButton linkTo="/">Go back</DeprecatedButton>
      </Card>
    );
  } else if (hasUnavailableError) {
    content = (
      <Card>
        <Icon type={WarningIcon} size="l" color="orange40" margin="0 0 m 0" />
        <Heading margin="0 0 s 0">NHS login is currently unavailable</Heading>
        <Text margin="0 0 m 0">
          This service may be affected by scheduled maintenance. You can check
          the current status of NHS login on NHS Digital.
        </Text>
        <DeprecatedButton
          variant="secondary"
          onClick={() => setHasUnavailableError(false)}
          externalHref="https://digital.nhs.uk/services/secondary-uses-service-sus/secondary-uses-service-sus-what-s-new/service-announcements-and-outages"
        >
          Check NHS login status
        </DeprecatedButton>
      </Card>
    );
  } else if (hasGenericError) {
    content = (
      <Card>
        <Icon type={WarningIcon} size="l" color="orange40" margin="0 0 m 0" />
        <Heading margin="0 0 s 0">Sorry, something went wrong</Heading>
        <Text margin="0 0 m 0">
          We're working on getting things fixed as soon as possible. Please try
          again later, or try signing up with your email address.
        </Text>
        <DeprecatedButton linkTo="/sign-up/select-gp">
          Sign up with your email address
        </DeprecatedButton>
      </Card>
    );
  } else {
    let message =
      authSource !== AuthSource.WEB
        ? "Wait while we process your request or click below to continue"
        : "Connecting with the NHS";

    let continueButtonAction = "";
    if (authSource === AuthSource.ANDROID) {
      continueButtonAction = `echo://link${props.history.location.search}`;
    } else if (
      authSource === AuthSource.IOS &&
      config.NHS_LOGIN_NATIVE_LINKS_HOST !== ""
    ) {
      continueButtonAction = `https://${config.NHS_LOGIN_NATIVE_LINKS_HOST}/auth/nhs-login${props.history.location.search}`;
    }

    content = (
      <LoaderCard>
        <Stack vertical={true} distribution="center">
          <LoadingIndicator margin="0 0 m 0" />
          <Text as="span" align="center">
            {message}
          </Text>
          {continueButtonAction !== "" && (
            <DeprecatedButton margin="l 0" externalHref={continueButtonAction}>
              Continue
            </DeprecatedButton>
          )}
        </Stack>
      </LoaderCard>
    );
  }

  return (
    <>
      <Helmet>
        <title>NHS Login Processing | Echo</title>
      </Helmet>
      <PageWrapper
        bgColor="gray"
        header={<Header />}
        footer={<PrimaryFooter />}
      >
        <CardWrapper>{content}</CardWrapper>
      </PageWrapper>
    </>
  );
};

export default NHSLoginAuth;
